/**
 * The client store (PXL — Player Experience Layer).
 *
 * A single observable cache fed by `GameConnection.onAny`: every inbound server
 * message is routed by its `t` tag into a named slice. Panels and the HUD read
 * slices through `useGame()` (a `useSyncExternalStore` subscription) and re-render
 * when the version bumps. NONE of this rides the per-tick movement path — the
 * data backbone is the private pushes / RPC replies / event channel (AD-031).
 *
 * Slice types are DERIVED from the wire union (`Payload<'inventorySnapshot'>` …)
 * so the store cannot drift from the protocol: if a payload changes shape, the
 * compiler flags the slice. The store holds presentation state only; it changes
 * no gameplay, economy, scarcity, progression or monetisation rule.
 */

import type {
  AuctionWire,
  CapacityKey,
  ChatMessagePayload,
  EntityDelta,
  GameEvent,
  HeldMailSummary,
  JoinFriendResultPayload,
  ListingWire,
  MasteryTrackWire,
  NodeWire,
  ReputationTrackWire,
  ServerMessage,
  StallWire,
} from '@great-cloud/shared/client';

import { gatherChannelMs } from '@great-cloud/shared/client';

import { ownBodyIn } from '../game/dungeon/run.js';
import { liveToasts, nextToastExpiry, pushToast } from './toasts.js';
import type { Toast } from './toasts.js';
import {
  pickSoftTarget,
  ThreatMemory,
  type HostileView,
  type SoftTargetPick,
} from '../game/combat/softTarget.js';
import { explicitTarget } from '../game/combat/explicitTarget.js';
import type { CraftRecipeView } from '../ui/panels/craftable.js';
import { newlyTurnedIn, reconcileDialogue } from '../ui/panels/questDialogue.js';
import { gameFeedback } from '../game/feedback/feedbackBus.js';
import { questSignals } from '../ui/questSignals.js';
import {
  capacityRefusalText,
  capacityWarnings,
  heldMailWarning,
  type CapacityWarnState,
} from '../ui/panels/capacity.js';
import { describeItemUseDenial, itemUsedText } from '../ui/panels/consumable.js';
import type { ConsumableReadyAt } from '../ui/panels/consumable.js';
import {
  abilityRefusalToasts,
  armAbilityCooldown,
  attackRefusalToasts,
  cooldownsFromLoadout,
  type AbilityCooldowns,
} from '../ui/abilitySlotState.js';
import { describeRepairDenial } from '../ui/panels/repairable.js';
import { describeSellDenial } from '../ui/panels/sellable.js';
import { capacityUpgradedText, describeCapacityUpgradeDenial } from '../ui/panels/capacity.js';
import { describeTravelDenial, shrineLabel } from '../ui/panels/shrineTravel.js';
import { describeDungeonDenial, describeDungeonExit } from '../ui/panels/dungeonEntry.js';
import { craftRefusalText, gatherRefusalText, handSwapToast } from './handSwap.js';
import { humanizeId, nameOf, reasonWords, stackName } from '../ui/playerText.js';

/** The own gather channel's presentation (AD-665). `ok: true` makes it a rig action moment. */
export interface GatherChannelView {
  ok: true;
  nodeId: number;
  resource: string;
  /** The node's gathering skill (absent when the pack does not know the resource). */
  gatheringSkill?: string;
  /** `performance.now()` at the press. */
  startedAtMs: number;
  /** The channel length (shared `gatherChannelMs` — the same number the server uses). */
  durationMs: number;
}

/** A spend-bearing request the panels must not send twice while it is unanswered. */
export type InFlightKind = 'craft' | 'mailSend' | 'itemUse' | 'bugReport';

/** An unanswered request stops blocking after this long (a lost answer must not wedge the button). */
export const IN_FLIGHT_TIMEOUT_MS = 10_000;

/**
 * A gather channel whose answer never came (lost with a socket, or a server that dropped
 * it) stops blocking the next E press this long after its own length ran out.
 */
export const GATHER_CHANNEL_GRACE_MS = 5_000;

/** The payload type carried by a given server message `t`. */
export type Payload<T extends ServerMessage['t']> = Extract<ServerMessage, { t: T }>['d'];

/** A light, merge-friendly view of a zone entity (for target frames + minimap). */
export interface EntityView {
  id: number;
  kind?: string;
  name?: string;
  x?: number;
  z?: number;
  facing?: number;
  health?: number;
  healthMax?: number;
  /** AD-833: present only on a summoned image — its caster's entity id (full state only). */
  summonOf?: number;
}

export type { Toast } from './toasts.js';

export interface LogLine {
  id: number;
  at: number;
  text: string;
  kind: string;
}

export interface BuffEntry {
  entityId: number;
  abilityId: string;
  stat: string;
  magnitude: number;
  isDebuff: boolean;
  /** Server tick the effect expires (startTick + durationTicks); 0 if unknown. */
  expiresTick: number;
}

/** Per-instance loot items collapsed into (contentId, rarity) display stacks. */
export interface LootItemGroup {
  contentId: string;
  rarity: string;
  count: number;
}

/**
 * One kill's loot, kept in a short feed for the HUD (UIE-307, gap D-11). Built
 * from the `lootDropped` event the server raises after drops are persisted
 * (UIP-5). Presentation only — the authoritative grant already landed in the
 * inventory; this is just the "what did I get" feedback the matrix asked for.
 */
export interface LootFeedEntry {
  id: number;
  at: number;
  /** True when the player landed the kill (`lootDropped.by === selfEntityId`). */
  mine: boolean;
  items: LootItemGroup[];
  resources: { contentId: string; quantity: number }[];
}

/** Collapse per-instance loot items into (contentId, rarity) display stacks. */
export function summariseLootItems(
  items: { contentId: string; rarity: string }[],
): LootItemGroup[] {
  const byKey = new Map<string, LootItemGroup>();
  for (const it of items) {
    const key = `${it.contentId}\u0000${it.rarity}`; // id-ok: grouping key
    const g = byKey.get(key);
    if (g) g.count += 1;
    else byKey.set(key, { contentId: it.contentId, rarity: it.rarity, count: 1 });
  }
  return [...byKey.values()];
}

/**
 * AD-332 / A1 gate refusals arrive as reason codes; show human words, never the
 * raw code (design §5.8 item 7). Unmapped reasons fall through unchanged.
 */
function why(reason: string | undefined): string {
  if (reason === 'mfaRequired')
    return 'two-factor authentication required — enable it under Account → Two-factor authentication';
  if (reason === 'notUnlocked') return 'account not yet unlocked for the player economy';
  // Fail-closed A1 check (AD-571): the server could not decide, the account did nothing wrong.
  if (reason === 'gateUnavailable') return 'the player economy is briefly unavailable — try again';
  const recipeLine = recipeLineRefusalText(reason);
  if (recipeLine) return recipeLine;
  return reasonWords(reason);
}

/**
 * The words for a recipe-line refusal (S5 production ceiling, AD-729): a line
 * that reached its ceiling has retired for good, and its successor opens only
 * then. Craft and workshop answer with different wire names for the same two
 * states. `null` for any other reason.
 */
export function recipeLineRefusalText(reason: string | undefined): string | null {
  if (reason === 'retired' || reason === 'recipeRetired') {
    return 'this recipe has retired — its line reached its production limit';
  }
  if (reason === 'notYetOpen' || reason === 'recipeNotYetOpen') {
    return 'this recipe is not open yet — it opens when the recipe before it retires';
  }
  return null;
}

/** The one line a refused craft shows the player (toast + crafting panel). */
/**
 * A refusal's words: an SP-5 `capacityExceeded` names its cap (AD-775 §8), any
 * other reason keeps the caller's words.
 */
function refusal(
  d: { reason?: string; cap?: CapacityKey; partner?: boolean },
  otherwise: (reason: string | undefined) => string = why,
): string {
  return d.reason === 'capacityExceeded'
    ? capacityRefusalText(d.cap, d.partner)
    : otherwise(d.reason);
}

export function describeCraftFailure(reason: string | undefined): string {
  return recipeLineRefusalText(reason) ?? craftRefusalText(reason) ?? reasonWords(reason);
}

/** A compact one-line loot summary for the combat log / toast. */
export function summariseLoot(
  items: { contentId: string; rarity: string }[],
  resources: { contentId: string; quantity: number }[],
): string {
  const parts: string[] = [];
  for (const g of summariseLootItems(items)) {
    const name = stackName(g.contentId);
    parts.push(g.count > 1 ? `${name} ×${g.count}` : name);
  }
  for (const r of resources) parts.push(`${stackName(r.contentId)} ×${r.quantity}`);
  return parts.join(', ');
}

/**
 * AD-798 (S2b): the words of a clear that paid the dungeon's completion reward —
 * reputation first (`+100 the-herd reputation`, as the reputation panel names a
 * faction), then any items. A reputation leg the daily cap fully absorbed arrives
 * as no line, so an all-capped reward reads as the cap, never as nothing.
 */
export function describeCompletionReward(d: {
  items: { contentId: string; rarity: string }[];
  resources: { contentId: string; quantity: number }[];
  reputation?: { factionId: string; amount: number }[];
}): string {
  const parts = (d.reputation ?? []).map(
    (r) => `+${r.amount} ${nameOf('factions', r.factionId)} reputation`,
  );
  const loot = summariseLoot(d.items, d.resources);
  if (loot) parts.push(loot);
  return parts.length > 0 ? parts.join(', ') : 'reputation at today’s cap';
}

/**
 * The one line a refused shelf shows the player (AD-403 typed denials).
 *
 * `reputationLocked` is the only reason that carries a requirement, and the rank
 * pair is rendered as the "N of M" the story asked for — `currentRank` is the
 * CALLER's own rank (R-1, scoped to the caller), so no other player's standing is
 * implied. The other two reasons say what they are and nothing more: neither
 * leaks anything the world does not already show.
 */
export function describeVendorDenial(d: {
  reason: 'unknownNpc' | 'notAService' | 'reputationLocked';
  requirement?: { requiredRankName: string; requiredRank: number; currentRank: number };
}): string {
  if (d.reason === 'unknownNpc') return 'No such NPC here.';
  if (d.reason === 'notAService') return 'This NPC keeps no shelf.';
  const r = d.requirement;
  if (!r) return 'Reputation locked.';
  return `Requires ${r.requiredRankName} — ${r.currentRank} of ${r.requiredRank}.`;
}

/** The full reactive client state. Every field is read by some panel/HUD surface. */
export interface ClientState {
  // session / world
  ack: Payload<'helloAck'> | null;
  selfEntityId: number | null;
  serverTick: number;
  entities: Map<number, EntityView>;
  /** Live resource nodes — seeded from helloAck; depleted/respawn tracked from events. */
  nodes: NodeWire[];
  /**
   * In a dungeon run (`dungeonResult` entered → left; any ack ends it). The entities are
   * then the instance's, in the plan's frame — the ack's world nodes, NPCs and shrines
   * are not, so nothing measures or plots them against the body (`ui/worldFrame.ts`).
   */
  inDungeonRun: boolean;
  /** Target the player has selected in the HUD (client-local). */
  targetId: number | null;

  // self / progression
  wallet: Payload<'walletState'> | null;
  stats: Payload<'characterStats'> | null;
  level: Payload<'levelState'> | null;
  stamina: Payload<'staminaState'> | null;
  loadout: Payload<'loadoutState'> | null;
  inventory: Payload<'inventorySnapshot'> | null;
  quests: Payload<'questLog'> | null;
  masteries: MasteryTrackWire[];
  reputation: ReputationTrackWire[];
  buffs: BuffEntry[];

  // world interaction
  dialogue: Payload<'npcDialogue'> | null;
  /** Counts conversations opened (npcDialogue pushes); the voice layer plays each once. */
  dialogueTalk: number;
  /**
   * The last confirmed turn-in (`questTurnedIn`, q-demo-dialogue-playtest-2026-10-29): the HUD's
   * completion card and the NPC's `complete` line. `seq` counts turn-ins, so each is shown and
   * spoken once; null until the first, and again once the card is dismissed.
   */
  questCompletion: (Payload<'questTurnedIn'> & { seq: number }) | null;
  /**
   * The last vendor shelf the player asked for (AD-403 `vendorList` → `vendorCatalogue`).
   * Holds the granted shelf OR the typed denial, verbatim — display only: the client
   * never filters lines, never prices an `unpriced` line, and there is no buy verb
   * (a purchase is its own story with its own SEC-3 review).
   */
  vendor: Payload<'vendorCatalogue'> | null;
  lastGather: Payload<'gatherResult'> | null;
  /**
   * The own gather CHANNEL in progress (AD-665, docs/systems/gather-channel.md §7): set on
   * the E press, cleared by the channel's `gatherResult` (the yield, a refusal, or
   * `interrupted`). Drives the HUD progress bar, the gather clip and the tool prop — the
   * clip plays DURING the channel, the toast still comes from the result.
   */
  gatherChannel: GatherChannelView | null;
  /**
   * Requests awaiting their answer (q-p2triage-fe-player-trust-and-ux): kind → when it was
   * sent. A second click while one is in flight would spend twice (a double craft, a mail
   * sent twice with its Shards); `beginRequest` refuses it. Cleared by the answer, by a
   * (re)connect ack (an answer lost with the socket never comes) or by `IN_FLIGHT_TIMEOUT_MS`.
   */
  inFlight: Partial<Record<InFlightKind, number>>;
  /**
   * Consumable cooldown groups still cooling (consumables.md §4; AD-791): group →
   * `Date.now()` when it is ready, from `itemUseResult.readyInMs`. The bag's Use
   * button reads it for its readout; the server stays the authority (an `onCooldown`
   * refusal re-arms it). Cleared on a (re)connect ack, like `inFlight`.
   */
  consumableReadyAt: ConsumableReadyAt;
  /**
   * Ability cooldowns still running (AD-849): id → when it is ready, out of how long, from the
   * server's `readyInMs` (`abilityResult`, each `loadoutState` ability). The HUD slots draw
   * their readiness fill from it; a press is never blocked on it (the server judges). Cleared on a
   * (re)connect ack and on the own respawn (the server clears its own then).
   */
  abilityCooldowns: AbilityCooldowns;
  /** Resource id → gathering skill, from the client pack (the channel length is per skill). */
  resourceSkills: Readonly<Record<string, string>>;
  /** Resource id → content tier, from the client pack (the guest prompt's rank-lock read). */
  resourceTiers: Readonly<Record<string, number>>;
  lastCraft: Payload<'craftResult'> | null;
  /**
   * The client pack's recipe projection (inputs/station/rank — `client-pack.ts`), set by
   * App once the pack loads (sprint-assembly-1: the crafting panel's "Craftable now").
   */
  recipes: CraftRecipeView[];

  // direct trade
  tradeRequest: Payload<'tradeRequested'> | null;
  trade: Payload<'tradeState'> | null;

  // economy
  marketSearch: ListingWire[];
  marketMine: ListingWire[];
  priceHistory: Payload<'priceHistory'> | null;
  auctionSearch: AuctionWire[];
  auctionMine: AuctionWire[];
  stallSearch: StallWire[];
  stallMine: StallWire[];

  // social / instances
  chat: ChatMessagePayload[];
  friends: Payload<'friendsList'> | null;
  guild: Payload<'guildState'> | null;
  mail: Payload<'mailbox'> | null;
  house: Payload<'houseState'> | null;
  workshop: Payload<'workshopState'> | null;
  arena: Payload<'arenaState'> | null;
  arenaLeaderboard: Payload<'arenaLeaderboard'> | null;
  raid: Payload<'raidState'> | null;
  season: Payload<'seasonState'> | null;

  // storefront (REST, set by the Store panel)
  storeCatalogue: unknown | null;
  gleamWallet: unknown | null;

  // feedback
  toasts: Toast[];
  combatLog: LogLine[];
  eventLog: LogLine[];
  /** Recent loot drops from the player's own kills (UIE-307, gap D-11). */
  loot: LootFeedEntry[];
  /** Total messages applied — a cheap "is the socket alive" signal for Debug. */
  messageCount: number;
}

function initialState(): ClientState {
  return {
    ack: null,
    selfEntityId: null,
    serverTick: 0,
    entities: new Map(),
    nodes: [],
    inDungeonRun: false,
    targetId: null,
    wallet: null,
    stats: null,
    level: null,
    stamina: null,
    loadout: null,
    inventory: null,
    quests: null,
    masteries: [],
    reputation: [],
    buffs: [],
    dialogue: null,
    dialogueTalk: 0,
    questCompletion: null,
    vendor: null,
    lastGather: null,
    gatherChannel: null,
    inFlight: {},
    consumableReadyAt: {},
    abilityCooldowns: {},
    resourceSkills: {},
    resourceTiers: {},
    lastCraft: null,
    recipes: [],
    tradeRequest: null,
    trade: null,
    marketSearch: [],
    marketMine: [],
    priceHistory: null,
    auctionSearch: [],
    auctionMine: [],
    stallSearch: [],
    stallMine: [],
    chat: [],
    friends: null,
    guild: null,
    mail: null,
    house: null,
    workshop: null,
    arena: null,
    arenaLeaderboard: null,
    raid: null,
    season: null,
    storeCatalogue: null,
    gleamWallet: null,
    toasts: [],
    combatLog: [],
    eventLog: [],
    loot: [],
    messageCount: 0,
  };
}

const CHAT_CAP = 200;
const LOG_CAP = 120;
const LOOT_CAP = 6;

export class ClientStore {
  state: ClientState = initialState();
  private version = 0;
  private listeners = new Set<() => void>();
  /** q-combat-game-feel: every `abilityResult`, for the HUD's input buffer (presentation only). */
  private abilityResultListeners = new Set<(d: Payload<'abilityResult'>) => void>();
  private nextId = 1;
  /** Turn-ins confirmed this session (`questCompletion.seq`); never reset by a dismissal. */
  private completionSeq = 0;
  /** SP-5 warning pacing for this session (capacity.ts). */
  private capacityWarned: CapacityWarnState = {};
  /** The §5.2 hold last warned about this session (H-1375); null = none yet. */
  private heldWarned: HeldMailSummary | null = null;
  /** Hostiles that recently threatened us — the soft target's first choice (combat.md §Control model). */
  private readonly threats = new ThreatMemory();

  /**
   * The soft target an ability press names (combat.md §Control model; combat-depth.md
   * §3.2): the explicit (clicked/tapped) target while it is legal, else threat-first, else the nearest live enemy within `SOFT_TARGET_RADIUS_M`.
   */
  softTarget(nowMs: number = performance.now()): SoftTargetPick | null {
    this.threats.prune(nowMs);
    const hostiles: HostileView[] = [];
    for (const e of this.state.entities.values()) {
      if (e.kind !== 'enemy' || e.x == null || e.z == null) continue;
      hostiles.push(
        e.health === undefined
          ? { id: e.id, x: e.x, z: e.z }
          : { id: e.id, x: e.x, z: e.z, health: e.health },
      );
    }
    return pickSoftTarget({
      self: this.selfPoint(),
      hostiles,
      threats: this.threats,
      nowMs,
      // AD-817: a clicked/tapped hostile wins for every ability press too.
      explicitTargetId: explicitTarget.id,
    });
  }

  /** Our own body's last known ground position, or null before it is known. */
  private selfPoint(): { x: number; z: number } | null {
    const id = this.state.selfEntityId;
    const self = id != null ? this.state.entities.get(id) : undefined;
    return self && self.x != null && self.z != null ? { x: self.x, z: self.z } : null;
  }

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  /** Hear every `abilityResult` (the ability input buffer re-holds an early press once). */
  onAbilityResult = (cb: (d: Payload<'abilityResult'>) => void): (() => void) => {
    this.abilityResultListeners.add(cb);
    return () => this.abilityResultListeners.delete(cb);
  };

  getVersion = (): number => this.version;

  private bump(): void {
    this.version += 1;
    for (const cb of this.listeners) cb();
  }

  /**
   * The player said goodbye (q-demo-dialogue-playtest-2026-10-29): the conversation is over, so a
   * later look at the Quests panel shows the log, not a conversation that has ended.
   */
  endConversation(): void {
    if (this.state.dialogue === null) return;
    this.state.dialogue = null;
    this.bump();
  }

  /** The completion card was dismissed (or timed out). */
  dismissCompletion(seq: number): void {
    if (this.state.questCompletion?.seq !== seq) return;
    this.state.questCompletion = null;
    this.bump();
  }

  /** Replace state on disconnect/leave so a re-entry starts clean. */
  reset(): void {
    this.state = initialState();
    this.bump();
  }

  /**
   * Push a transient toast (UI feedback for RPC results / notices). It ages out on its own
   * (toasts.ts `TOAST_TTL_MS`); a repeat with the same `key` (default: same kind + words)
   * refreshes the live one and counts ×n instead of stacking (q-hud-toasts-never-expire).
   */
  toast(kind: Toast['kind'], text: string, key?: string): void {
    const now = Date.now();
    this.state.toasts = pushToast(this.state.toasts, { id: this.nextId++, kind, text, key }, now);
    this.scheduleToastExpiry(now);
    this.bump();
  }

  /**
   * An `abilityResult` (AD-849): a cast is logged and arms the slot's cooldown; a refusal the
   * slot shows itself (cooldown, stamina) updates the slot and raises NO toast — the owner's
   * "remove the need for the text pop ups around cooldowns". Any other refusal still toasts, one
   * row per ability (a repeat refreshes it ×n).
   */
  private applyAbilityResult(d: Payload<'abilityResult'>): void {
    for (const cb of this.abilityResultListeners) cb(d);
    const now = Date.now();
    const cooldownMs = this.state.loadout?.abilities?.find((a) => a.id === d.abilityId)?.cooldownMs;
    if (d.readyInMs !== undefined)
      this.state.abilityCooldowns = armAbilityCooldown(
        this.state.abilityCooldowns,
        d.abilityId,
        d.readyInMs,
        now,
        cooldownMs,
      );
    if (
      !d.ok &&
      d.reason === 'insufficientStamina' &&
      d.staminaAfter !== undefined &&
      this.state.stamina
    )
      this.state.stamina = { ...this.state.stamina, current: d.staminaAfter };
    if (d.ok) {
      this.log(
        'combatLog',
        'ability',
        `${humanizeId(d.abilityId)}${d.damage ? ` ${d.damage} dmg` : ''}${d.healed ? ` ${d.healed} heal` : ''}`,
      );
    } else if (abilityRefusalToasts(d.reason)) {
      this.toast(
        'bad',
        `${humanizeId(d.abilityId)}: ${reasonWords(d.reason)}`,
        `ability:${d.abilityId}`, // id-ok: toast dedupe key
      );
    }
  }

  dismissToast(id: number): void {
    this.state.toasts = this.state.toasts.filter((t) => t.id !== id);
    this.bump();
  }

  /** Drop the toasts that aged out by `now`; re-arms the timer for the next one. */
  pruneToasts(now: number = Date.now()): void {
    const live = liveToasts(this.state.toasts, now);
    if (live !== this.state.toasts) {
      this.state.toasts = live;
      this.bump();
    }
    this.scheduleToastExpiry(now);
  }

  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  /** One timer for the soonest expiry; none while no toast is up. */
  private scheduleToastExpiry(now: number): void {
    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
    this.toastTimer = null;
    const wait = nextToastExpiry(this.state.toasts, now);
    if (wait === null) return;
    const timer = setTimeout(() => {
      this.toastTimer = null;
      this.pruneToasts();
    }, wait + 1);
    // Never hold a Node test process open on a toast (the browser has no unref).
    (timer as { unref?: () => void }).unref?.();
    this.toastTimer = timer;
  }

  /** Client-local target selection (HUD). */
  setTarget(id: number | null): void {
    this.state.targetId = id;
    this.bump();
  }

  /** The loaded content pack's resource → gathering skill map (App sets it after each sync). */
  setResourceSkills(skills: Readonly<Record<string, string>>): void {
    this.state.resourceSkills = skills;
    this.bump();
  }

  /** The loaded content pack's resource → tier map (App sets it beside the skills). */
  setResourceTiers(tiers: Readonly<Record<string, number>>): void {
    this.state.resourceTiers = tiers;
    this.bump();
  }

  /**
   * Start presenting a gather channel on the E press (AD-665). Returns whether the caller
   * should send the `interactIntent` — `false` while one is already running (the server
   * would refuse it `tooSoon`). The server's `gatherResult` ends it.
   */
  beginGatherChannel(nodeId: number, resource: string, nowMs: number): boolean {
    const running = this.state.gatherChannel;
    if (
      running !== null &&
      nowMs - running.startedAtMs < running.durationMs + GATHER_CHANNEL_GRACE_MS
    )
      return false;
    const gatheringSkill = this.state.resourceSkills[resource] || undefined;
    this.state.gatherChannel = {
      ok: true,
      nodeId,
      resource,
      ...(gatheringSkill !== undefined ? { gatheringSkill } : {}),
      startedAtMs: nowMs,
      durationMs: gatherChannelMs(gatheringSkill),
    };
    gameFeedback.emit({ kind: 'gatherStrike', nodeId, resource });
    this.bump();
    return true;
  }

  /**
   * Claim the one in-flight slot for `kind` before sending it (q-p2triage-fe-player-trust-
   * and-ux). Returns whether the caller should send: `false` while an earlier one is still
   * unanswered (and younger than `IN_FLIGHT_TIMEOUT_MS`), so a double-click spends once.
   */
  beginRequest(kind: InFlightKind, nowMs: number): boolean {
    const at = this.state.inFlight[kind];
    if (at !== undefined && nowMs - at < IN_FLIGHT_TIMEOUT_MS) return false;
    this.state.inFlight = { ...this.state.inFlight, [kind]: nowMs };
    this.bump();
    return true;
  }

  /** Whether `kind` is awaiting its answer at `nowMs` (the panels disable their button). */
  isInFlight(kind: InFlightKind, nowMs: number): boolean {
    const at = this.state.inFlight[kind];
    return at !== undefined && nowMs - at < IN_FLIGHT_TIMEOUT_MS;
  }

  private endRequest(kind: InFlightKind): void {
    if (this.state.inFlight[kind] === undefined) return;
    const next = { ...this.state.inFlight };
    delete next[kind];
    this.state.inFlight = next;
  }

  /** The loaded content pack's recipes (App sets them after each pack sync). */
  setRecipes(recipes: CraftRecipeView[]): void {
    this.state.recipes = recipes;
    this.bump();
  }

  /** Storefront REST results live outside the socket; the Store panel sets them. */
  setStoreCatalogue(v: unknown): void {
    this.state.storeCatalogue = v;
    this.bump();
  }

  setGleamWallet(v: unknown): void {
    this.state.gleamWallet = v;
    this.bump();
  }

  private log(channel: 'combatLog' | 'eventLog', kind: string, text: string): void {
    const line: LogLine = { id: this.nextId++, at: Date.now(), text, kind };
    this.state[channel] = [...this.state[channel], line].slice(-LOG_CAP);
  }

  /** Route one inbound server message into the slices. Bumps once per message. */
  apply(msg: ServerMessage): void {
    this.state.messageCount += 1;
    if ('tick' in msg && typeof msg.tick === 'number') this.state.serverTick = msg.tick;

    switch (msg.t) {
      case 'helloAck':
      case 'resumeAck': {
        this.state.ack = msg.d;
        this.state.selfEntityId = msg.d.entityId;
        this.threats.clear(); // a new join's ids are a new space
        this.state.nodes = msg.d.nodes.map((n) => ({ ...n }));
        this.state.inDungeonRun = false;
        this.mergeEntity({ ...msg.d.you });
        // Answers owed to the old socket never arrive: a channel or request still showing
        // would block the next E press / Craft / Send for good.
        this.state.gatherChannel = null;
        this.state.inFlight = {};
        this.state.consumableReadyAt = {};
        this.state.abilityCooldowns = {};
        break;
      }
      case 'snapshot': {
        // A full snapshot is the authoritative entity set (as in Game.onSnapshot): drop what it
        // does not carry. With area-of-interest culling (WR.5) a resume's full snapshot omits
        // entities that left interest while the socket was away — without this they would stay
        // on the minimap, which reads this store, as ghosts.
        if (msg.d.full) {
          const carried = new Set(msg.d.entities.map((e) => e.id));
          for (const id of this.state.entities.keys()) {
            if (!carried.has(id)) this.state.entities.delete(id);
          }
          // AD-705: a run's zone (and the world's again on the way out) allocates its own
          // ids and sends no ack — follow our body by name, as Game.rebindOwnBody does.
          const mine = ownBodyIn(msg.d.entities, this.state.ack?.you.name ?? null);
          if (mine !== null && mine !== this.state.selfEntityId) {
            this.state.selfEntityId = mine;
            this.threats.clear(); // a run swap renumbers every id
          }
        }
        for (const e of msg.d.entities) this.mergeEntity(e);
        for (const id of msg.d.removed) this.state.entities.delete(id);
        this.state.entities = new Map(this.state.entities);
        break;
      }
      case 'event':
        this.applyEvent(msg.d);
        break;
      case 'systemNotice':
        this.toast('info', msg.d.message);
        this.log('eventLog', 'notice', msg.d.message);
        break;
      case 'walletState':
        this.state.wallet = msg.d;
        break;
      case 'characterStats':
        this.state.stats = msg.d;
        break;
      case 'inventorySnapshot': {
        // AD-844: the server swaps tool <-> weapon inside a gather / attack; say what is in hand.
        const swapped = handSwapToast(
          this.state.inventory,
          msg.d,
          this.state.ack ? String(this.state.ack.characterId) : undefined,
        );
        this.state.inventory = msg.d;
        if (swapped) this.toast('info', swapped, 'hand-swap');
        // SP-5 approach warning (AD-775 §8): paced per cap per session.
        const warned = capacityWarnings(msg.d.capacity, this.capacityWarned);
        this.capacityWarned = warned.next;
        for (const text of warned.texts) this.toast('bad', text);
        const held = heldMailWarning(msg.d.capacity?.held, this.heldWarned);
        this.heldWarned = held.next;
        if (held.text) this.toast('bad', held.text);
        break;
      }
      case 'levelState':
        this.state.level = msg.d;
        if (msg.d.leveledUp) {
          this.toast('good', `Level up — now level ${msg.d.level}!`);
          gameFeedback.emit({ kind: 'levelUp', level: msg.d.level });
        }
        break;
      case 'staminaState':
        this.state.stamina = msg.d;
        break;
      case 'loadoutState':
        this.state.loadout = msg.d;
        // AD-849: the push states every loadout ability's cooldown left — the whole truth.
        if (msg.d.abilities)
          this.state.abilityCooldowns = cooldownsFromLoadout(msg.d.abilities, Date.now());
        break;
      case 'masteryState':
        this.mergeMasteries(msg.d.tracks);
        if (msg.d.rankedUp) this.toast('good', 'Mastery rank up!');
        break;
      case 'reputationState':
        this.mergeReputation(msg.d.tracks);
        if (msg.d.rankedUp) this.toast('good', 'Reputation rank up!');
        break;
      case 'questLog':
        // q-o071-playtest-fixes (a): the open dialogue follows the log, or its turn-in
        // button never appears (and a spent one lingers) until the player talks again.
        // The turn-in's words are the completion card's (`questTurnedIn`), not a toast.
        for (const name of newlyTurnedIn(this.state.quests, msg.d)) {
          gameFeedback.emit({ kind: 'questComplete', name });
        }
        // AD-867: an objective met / a quest ready to hand in is a moment, not a silent tick.
        for (const sig of questSignals(this.state.quests, msg.d)) {
          this.toast('good', sig.text);
          gameFeedback.emit(
            sig.kind === 'ready'
              ? { kind: 'questReady', name: sig.quest }
              : { kind: 'objectiveComplete', name: sig.quest },
          );
        }
        this.state.quests = msg.d;
        if (this.state.dialogue)
          this.state.dialogue = reconcileDialogue(this.state.dialogue, msg.d);
        break;
      case 'gatherResult':
        this.state.lastGather = msg.d;
        this.state.gatherChannel = null; // AD-665: the channel's answer ends its presentation
        gameFeedback.emit({
          kind: 'gather',
          nodeId: msg.d.nodeId,
          resource:
            msg.d.resource ?? this.state.nodes.find((n) => n.id === msg.d.nodeId)?.resource ?? '',
          ok: msg.d.ok,
        });
        if (!msg.d.ok && msg.d.reason === 'interrupted') this.toast('bad', 'Gather interrupted');
        else if (msg.d.ok)
          this.toast(
            'good',
            `Gathered ${msg.d.quantity ?? 1}× ${msg.d.resource ? nameOf('resources', msg.d.resource) : 'something'}`,
          );
        else
          this.toast(
            'bad',
            refusal(msg.d, (r) =>
              gatherRefusalText(
                r,
                this.state.resourceSkills[
                  this.state.nodes.find((n) => n.id === msg.d.nodeId)?.resource ?? ''
                ],
              ),
            ),
          );
        break;
      case 'craftResult':
        this.state.lastCraft = msg.d;
        this.endRequest('craft');
        gameFeedback.emit({ kind: 'craft', ok: msg.d.ok });
        if (msg.d.ok) this.toast('good', `Crafted ${msg.d.item?.name ?? msg.d.recipeId}`);
        else this.toast('bad', `Craft failed: ${refusal(msg.d, describeCraftFailure)}`);
        break;
      case 'attackResult':
        if (msg.d.ok)
          this.log(
            'combatLog',
            'attack',
            `Hit for ${msg.d.damage ?? 0}${msg.d.killed ? ' (killed)' : ''}`,
          );
        // AD-849: a mid-swing press raises no "Attack: onCooldown" toast (cooldown clutter).
        else if (attackRefusalToasts(msg.d.reason))
          this.toast('bad', `Attack: ${reasonWords(msg.d.reason)}`);
        break;
      case 'abilityResult':
        this.applyAbilityResult(msg.d);
        break;
      case 'npcDialogue': {
        // AD-870: a refresh of the open conversation (after a turn-in) updates it in place; only a
        // new talk counts as a new conversation, so what the NPC is saying is not cut off.
        const same = msg.d.refresh === true && this.state.dialogue?.npcId === msg.d.npcId;
        this.state.dialogue = msg.d;
        if (!same) this.state.dialogueTalk += 1;
        break;
      }
      case 'questTurnedIn':
        this.state.questCompletion = {
          ...msg.d,
          seq: (this.completionSeq += 1),
        };
        break;
      case 'vendorCatalogue':
        this.state.vendor = msg.d;
        break;
      case 'itemUseResult': {
        // consumables.md §4: health/stamina arrive on the snapshot/stamina pushes, the
        // buff as `buffApplied`, the bag on the inventory push; this says what happened
        // and arms the group's cooldown readout.
        this.endRequest('itemUse');
        const item = this.state.inventory?.items.find((i) => i.id === msg.d.itemId);
        const group = msg.d.ok ? msg.d.cooldownGroup : item?.cooldownGroup;
        const readyInMs = msg.d.readyInMs ?? 0;
        if (group && readyInMs > 0)
          this.state.consumableReadyAt = {
            ...this.state.consumableReadyAt,
            [group]: Date.now() + readyInMs,
          };
        if (msg.d.ok) this.toast('good', itemUsedText(msg.d, item?.name ?? msg.d.contentId));
        else this.toast('bad', describeItemUseDenial(msg.d.denied, msg.d.readyInMs));
        break;
      }
      case 'repairResult':
        // AD-672 S2: the durability + balance arrive on the inventory/wallet pushes
        // that follow a success; this only says what happened.
        if (msg.d.ok)
          this.toast(
            'good',
            msg.d.retired
              ? `Retired — worn past repair (◈ ${msg.d.shards})`
              : `Repaired to ${msg.d.durabilityCurrent}/${msg.d.durabilityMax} (◈ ${msg.d.shards})`,
          );
        else this.toast('bad', describeRepairDenial(msg.d.denied));
        break;
      case 'vendorSellResult':
        // AD-672 S3: the bag + balance arrive on the inventory/wallet pushes that
        // follow a success; this only says what happened.
        if (msg.d.ok) this.toast('good', `Sold for ◈ ${msg.d.shards}`);
        else this.toast('bad', describeSellDenial(msg.d.denied, msg.d.capRemaining, msg.d.cap));
        break;
      case 'capacityUpgradeResult':
        // AD-775 §6.1: the new gauges + next offer arrive on the inventory push and the
        // balance on the wallet push that follow a success; this only says what happened.
        if (msg.d.ok) this.toast('good', capacityUpgradedText(msg.d.cap, msg.d.size, msg.d.shards));
        else
          this.toast(
            'bad',
            describeCapacityUpgradeDenial(msg.d.denied, {
              cap: msg.d.cap,
              ...(msg.d.tier !== undefined ? { tier: msg.d.tier } : {}),
              ...(msg.d.openers ? { openers: msg.d.openers } : {}),
            }),
          );
        break;
      case 'travelResult': {
        // AD-672 S4: the move arrives on the full snapshot and the balance on the
        // wallet push that follow a success; this only says what happened.
        if (msg.d.ok) {
          const to = this.state.ack?.world.shrines.find((s) => s.id === msg.d.shrineId);
          this.toast(
            'good',
            `Travelled to ${to ? shrineLabel(to) : msg.d.shrineId} (◈ ${msg.d.shards})`,
          );
        } else this.toast('bad', describeTravelDenial(msg.d.denied));
        break;
      }
      case 'dungeonResult':
        // E4.6.S3 slice 2d: the swap is the Game's (runClient.ts); the body moves on
        // the full snapshot that follows. This only says what happened — and whether we
        // are in a run (the ack's world lists then stop describing where we stand).
        if (msg.d.ok) this.state.inDungeonRun = msg.d.action === 'entered';
        if (!msg.d.ok) this.toast('bad', describeDungeonDenial(msg.d.denied));
        else if (msg.d.action === 'entered') this.toast('info', 'You go down into the dungeon.');
        else
          this.toast(
            msg.d.outcome === 'cleared' ? 'good' : 'info',
            describeDungeonExit(msg.d.outcome),
          );
        break;
      case 'dungeonReward': {
        // AD-752 (`q-dungeon-clear-toast`): the clear's reward settles after the run's
        // teardown, so it is told apart from any other grant only by this push. The
        // items themselves arrive on the inventory push that follows.
        if (msg.d.unpaid) {
          // `q-dungeon-clear-unpaid-words`: a clear that paid nothing says why.
          const why =
            msg.d.unpaid === 'overBudget'
              ? 'no reward — today’s rewarded runs are spent; they refresh with the next UTC day'
              : 'no reward — the boss left nothing behind';
          this.log('combatLog', 'loot', `Dungeon cleared — ${why}`);
          this.toast('info', `Dungeon cleared — ${why}`);
          break;
        }
        if (msg.d.completion) {
          // AD-798 (S2b): the boss's pick was withheld, so the clear paid the dungeon's
          // fixed completion reward — told the way a turn-in tells its rewards.
          const words = describeCompletionReward(msg.d);
          if (msg.d.items.length + msg.d.resources.length > 0) {
            const feed: LootFeedEntry = {
              id: this.nextId++,
              at: Date.now(),
              mine: true,
              items: summariseLootItems(msg.d.items),
              resources: msg.d.resources.map((r) => ({ ...r })),
            };
            this.state.loot = [...this.state.loot, feed].slice(-LOOT_CAP);
          }
          this.log('combatLog', 'loot', `Dungeon completion reward: ${words}`);
          this.toast('good', `Dungeon cleared — completion reward: ${words}`);
          break;
        }
        const entry: LootFeedEntry = {
          id: this.nextId++,
          at: Date.now(),
          mine: true,
          items: summariseLootItems(msg.d.items),
          resources: msg.d.resources.map((r) => ({ ...r })),
        };
        this.state.loot = [...this.state.loot, entry].slice(-LOOT_CAP);
        const summary = summariseLoot(msg.d.items, msg.d.resources);
        this.log('combatLog', 'loot', `Dungeon reward: ${summary}`);
        this.toast('good', `Dungeon cleared — reward: ${summary}`);
        break;
      }
      case 'tradeRequested':
        this.state.tradeRequest = msg.d;
        this.toast('info', `${msg.d.fromName} wants to trade`);
        break;
      case 'tradeOpened':
        this.state.tradeRequest = null;
        this.toast('info', `Trading with ${msg.d.partnerName}`);
        break;
      case 'tradeState':
        this.state.trade = msg.d;
        break;
      case 'tradeClosed':
        this.state.trade = null;
        this.state.tradeRequest = null;
        if (msg.d.reason === 'capacityExceeded')
          this.toast('bad', `Trade refused: ${refusal(msg.d)}`);
        else this.toast(msg.d.reason === 'completed' ? 'good' : 'info', `Trade ${msg.d.reason}`);
        break;
      case 'marketSearchResult':
        this.state.marketSearch = msg.d.listings;
        break;
      case 'marketMine':
        this.state.marketMine = msg.d.listings;
        break;
      case 'priceHistory':
        this.state.priceHistory = msg.d;
        break;
      case 'marketListResult':
        this.toast(
          msg.d.ok ? 'good' : 'bad',
          msg.d.ok ? 'Listed for sale' : `List: ${why(msg.d.reason)}`,
        );
        break;
      case 'marketBuyResult':
        this.toast(msg.d.ok ? 'good' : 'bad', msg.d.ok ? 'Purchased' : `Buy: ${refusal(msg.d)}`);
        break;
      case 'marketCancelResult':
        this.toast(
          msg.d.ok ? 'info' : 'bad',
          msg.d.ok ? 'Listing cancelled' : `Cancel: ${msg.d.reason}`,
        );
        break;
      case 'auctionSearchResult':
        this.state.auctionSearch = msg.d.auctions;
        break;
      case 'auctionMine':
        this.state.auctionMine = msg.d.auctions;
        break;
      case 'auctionCreateResult':
        this.toast(
          msg.d.ok ? 'good' : 'bad',
          msg.d.ok ? 'Auction created' : `Auction: ${why(msg.d.reason)}`,
        );
        break;
      case 'auctionBidResult':
        this.toast(
          msg.d.ok ? 'good' : 'bad',
          msg.d.ok ? `Bid accepted (${msg.d.currentBid})` : `Bid: ${refusal(msg.d)}`,
        );
        break;
      case 'stallSearchResult':
        this.state.stallSearch = msg.d.stalls;
        break;
      case 'stallMine':
        this.state.stallMine = msg.d.stalls;
        break;
      case 'stallOpenResult':
        this.toast(
          msg.d.ok ? 'good' : 'bad',
          msg.d.ok ? 'Stall opened' : `Stall: ${why(msg.d.reason)}`,
        );
        break;
      case 'stallBuyResult':
        this.toast(
          msg.d.ok ? 'good' : 'bad',
          msg.d.ok ? 'Bought from stall' : `Stall buy: ${refusal(msg.d)}`,
        );
        break;
      case 'stallCloseResult':
        this.toast(
          msg.d.ok ? 'info' : 'bad',
          msg.d.ok ? 'Stall closed' : `Stall close: ${msg.d.reason}`,
        );
        break;
      case 'chatMessage':
        this.state.chat = [...this.state.chat, msg.d].slice(-CHAT_CAP);
        break;
      case 'friendsList':
        this.state.friends = msg.d;
        break;
      case 'presenceUpdate': {
        // The delta exists so the friends panel updates without a refetch (§4.2): apply it.
        const p = msg.d;
        if (this.state.friends) {
          this.state.friends = {
            ...this.state.friends,
            friends: this.state.friends.friends.map((f) =>
              f.accountId === p.accountId
                ? { ...f, name: p.name, status: p.status, location: p.location }
                : f,
            ),
          };
        }
        this.log('eventLog', 'presence', `${p.name} is ${p.status}`); // id-ok: 'online' | 'offline'
        break;
      }
      case 'joinFriendResult':
        this.toast(joinFriendMoved(msg.d.outcome) ? 'good' : 'info', joinFriendText(msg.d));
        break;
      case 'redirect':
        // A8b-2: the server is moving this player to the friend's channel process; the
        // connection reopens there on its own (net/connection.ts).
        this.toast(
          'good',
          `Joining ${msg.d.friendName ?? 'your friend'} (channel ${msg.d.channel})…`,
        );
        break;
      case 'socialActionResult':
        // q-bug-report-button: the report's answer reads as a sentence, not a verb + reason.
        if (msg.d.action === 'bugReport') {
          this.endRequest('bugReport');
          this.toast(
            msg.d.ok ? 'good' : 'bad',
            msg.d.ok
              ? 'Bug report sent — thank you.'
              : msg.d.reason === 'rateLimited'
                ? 'Too many bug reports for now — try again later.'
                : 'The bug report could not be saved — please try again.',
          );
          break;
        }
        this.toast(
          msg.d.ok ? 'info' : 'bad',
          `${msg.d.action}: ${msg.d.ok ? 'ok' : why(msg.d.reason ?? 'failed')}`,
        );
        break;
      case 'moderationNotice':
        this.toast('info', `Moderation: ${JSON.stringify(msg.d)}`);
        break;
      case 'mailbox':
        this.state.mail = msg.d;
        break;
      case 'mailActionResult':
        if (msg.d.action === 'mailSend') this.endRequest('mailSend');
        this.toast(
          msg.d.ok ? 'good' : 'bad',
          `Mail ${msg.d.action}: ${msg.d.ok ? 'ok' : refusal(msg.d)}`,
        );
        break;
      case 'guildState':
        this.state.guild = msg.d;
        break;
      case 'houseState':
        this.state.house = msg.d;
        break;
      case 'houseActionResult':
        this.toast(
          msg.d.ok ? 'good' : 'bad',
          `House ${msg.d.action}: ${msg.d.ok ? 'ok' : why(msg.d.reason ?? 'failed')}`,
        );
        break;
      case 'workshopState':
        this.state.workshop = msg.d;
        break;
      case 'workshopActionResult':
        this.toast(
          msg.d.ok ? 'good' : 'bad',
          `Workshop ${msg.d.action}: ${msg.d.ok ? 'ok' : refusal(msg.d, (r) => why(r ?? 'failed'))}`,
        );
        break;
      case 'arenaState':
        this.state.arena = msg.d;
        break;
      case 'arenaResult':
        this.toast(
          msg.d.won ? 'good' : 'bad',
          `Arena ${msg.d.format}: ${msg.d.won ? 'win' : 'loss'} (${msg.d.ratingAfter})`,
        );
        break;
      case 'arenaLeaderboard':
        this.state.arenaLeaderboard = msg.d;
        break;
      case 'arenaActionResult':
        this.toast(
          msg.d.ok ? 'info' : 'bad',
          `Arena ${msg.d.action}: ${msg.d.ok ? 'ok' : why(msg.d.reason ?? 'failed')}`,
        );
        break;
      case 'raidState':
        this.state.raid = msg.d;
        break;
      case 'raidResult':
        this.toast(
          msg.d.cleared ? 'good' : 'bad',
          `Raid ${humanizeId(msg.d.encounterId)}: ${msg.d.cleared ? 'cleared' : 'wipe'}`,
        );
        break;
      case 'raidActionResult':
        this.toast(
          msg.d.ok ? 'info' : 'bad',
          `Raid ${msg.d.action}: ${msg.d.ok ? 'ok' : why(msg.d.reason ?? 'failed')}`,
        );
        break;
      case 'seasonState':
        this.state.season = msg.d;
        break;
      case 'pong':
      case 'error':
      case 'kick':
        break;
    }
    this.bump();
  }

  private mergeEntity(delta: EntityDelta | (EntityView & { id: number })): void {
    const cur = this.state.entities.get(delta.id) ?? { id: delta.id };
    const next: EntityView = { ...cur };
    if ('kind' in delta && delta.kind !== undefined) next.kind = delta.kind;
    if (delta.name !== undefined) next.name = delta.name;
    if (delta.x !== undefined) next.x = delta.x;
    if (delta.z !== undefined) next.z = delta.z;
    if (delta.facing !== undefined) next.facing = delta.facing;
    if (delta.health !== undefined) next.health = delta.health;
    if (delta.healthMax !== undefined) next.healthMax = delta.healthMax;
    if (delta.summonOf !== undefined) next.summonOf = delta.summonOf;
    this.state.entities.set(delta.id, next);
  }

  /** Flip a node's depleted flag (nodeDepleted/nodeRespawned) so the HUD prompt + minimap track live availability. */
  private setNodeDepleted(nodeId: number, depleted: boolean): void {
    this.state.nodes = this.state.nodes.map((n) => (n.id === nodeId ? { ...n, depleted } : n));
    this.log(
      'eventLog',
      depleted ? 'nodeDepleted' : 'nodeRespawned',
      `node ${nodeId} ${depleted ? 'depleted' : 'respawned'}`,
    );
  }

  /** An entity as the combat log names it: its own name, "You", or a plain noun — never its number. */
  private entityLabel(entityId: number): string {
    if (entityId === this.state.selfEntityId) return 'You';
    return this.state.entities.get(entityId)?.name ?? 'Someone';
  }

  private applyEvent(ev: GameEvent): void {
    switch (ev.type) {
      // Soft target (combat.md §Control model): who is attacking us. No render — no bump needed
      // beyond the one apply() already does.
      case 'telegraph':
        this.threats.noteTelegraph(ev.sourceId, ev.shape, this.selfPoint(), performance.now());
        break;
      case 'telegraphResolve':
        if (this.state.selfEntityId != null)
          this.threats.noteResolve(
            ev.sourceId,
            ev.hits,
            this.state.selfEntityId,
            performance.now(),
          );
        break;
      case 'attack':
        if (this.state.selfEntityId != null)
          this.threats.noteAttack(
            ev.sourceId,
            ev.targetId,
            this.state.selfEntityId,
            performance.now(),
          );
        break;
      case 'buffApplied':
        this.state.buffs = [
          ...this.state.buffs.filter(
            (b) =>
              !(b.entityId === ev.entityId && b.abilityId === ev.abilityId && b.stat === ev.stat),
          ),
          {
            entityId: ev.entityId,
            abilityId: ev.abilityId,
            stat: ev.stat,
            magnitude: ev.magnitude,
            isDebuff: ev.isDebuff,
            expiresTick: this.state.serverTick + ev.durationTicks,
          },
        ];
        break;
      case 'buffExpired':
        this.state.buffs = this.state.buffs.filter(
          (b) =>
            !(b.entityId === ev.entityId && b.abilityId === ev.abilityId && b.stat === ev.stat),
        );
        break;
      case 'entityDied':
        this.log('combatLog', 'death', `${this.entityLabel(ev.entityId)} fell`);
        if (ev.entityId === this.state.selfEntityId) this.toast('bad', 'You were defeated');
        break;
      case 'playerRespawned':
        if (ev.entityId === this.state.selfEntityId) {
          // The server cleared every ability cooldown on the respawn (zone tickPlayerRespawns).
          this.state.abilityCooldowns = {};
          this.toast('info', 'Respawned');
        }
        break;
      case 'bossPhase':
        this.log('combatLog', 'boss', `${this.entityLabel(ev.entityId)} → phase ${ev.phaseIndex}`);
        break;
      case 'bossEnraged':
        this.log('combatLog', 'boss', `${this.entityLabel(ev.entityId)} is enraged`);
        break;
      case 'nodeDepleted':
        this.setNodeDepleted(ev.nodeId, true);
        break;
      case 'nodeRespawned':
        this.setNodeDepleted(ev.nodeId, false);
        break;
      case 'lootDropped': {
        // Only the player's OWN kills give actionable loot feedback (UIE-307);
        // other players' broadcast drops are not theirs and would be noise.
        if (ev.by == null || ev.by !== this.state.selfEntityId) break;
        if (ev.items.length === 0 && ev.resources.length === 0) break;
        const entry: LootFeedEntry = {
          id: this.nextId++,
          at: Date.now(),
          mine: true,
          items: summariseLootItems(ev.items),
          resources: ev.resources.map((r) => ({ ...r })),
        };
        this.state.loot = [...this.state.loot, entry].slice(-LOOT_CAP);
        const summary = summariseLoot(ev.items, ev.resources);
        this.log('combatLog', 'loot', `Loot: ${summary}`);
        this.toast('good', `Loot: ${summary}`);
        gameFeedback.emit({
          kind: 'loot',
          entityId: ev.entityId,
          rare: ev.items.some((i) => i.rarity !== 'common'),
        });
        break;
      }
      case 'listingCreated':
      case 'listingClosed':
      case 'auctionCreated':
      case 'auctionBid':
      case 'auctionClosed':
      case 'stallOpened':
      case 'stallClosed':
        this.log('eventLog', ev.type, ev.type);
        break;
      default:
        break;
    }
  }

  private mergeMasteries(tracks: MasteryTrackWire[]): void {
    const byId = new Map(this.state.masteries.map((t) => [t.trackId, t]));
    for (const t of tracks) byId.set(t.trackId, t);
    this.state.masteries = [...byId.values()];
  }

  private mergeReputation(tracks: ReputationTrackWire[]): void {
    const byId = new Map(this.state.reputation.map((t) => [t.factionId, t]));
    for (const t of tracks) byId.set(t.factionId, t);
    this.state.reputation = [...byId.values()];
  }
}

/** True when a join-a-friend outcome moved someone (AD-804). */
function joinFriendMoved(outcome: JoinFriendResultPayload['outcome']): boolean {
  return (
    outcome === 'joined' ||
    outcome === 'friendMoved' ||
    outcome === 'bothMoved' ||
    outcome === 'pulled'
  );
}

/** A join-a-friend outcome in plain words (AD-804 §2: "the player is told the outcome"). */
export function joinFriendText(d: JoinFriendResultPayload): string {
  const who = d.friendName ?? 'your friend';
  const where = d.channelId !== undefined ? ` (channel ${d.channelId})` : '';
  switch (d.outcome) {
    case 'joined':
      return `You joined ${who}${where}.`;
    case 'friendMoved':
      return `${who} joined you${where}.`;
    case 'bothMoved':
      return `You and ${who} moved to a quieter channel${where}.`;
    case 'together':
      return `You and ${who} are already together${where}.`;
    case 'pulled':
      return `${who} asked to play with you — you joined them${where}.`;
    case 'notFriend':
      return 'You can only join a friend.';
    case 'offline':
      return `${who} is not playing right now.`;
    case 'busy':
      return 'Not now — finish your fight (or theirs) and try again.';
    case 'onCooldown':
      return 'You moved a moment ago — try again shortly.';
    case 'noRoom':
      return 'The world is busy where your friend is — try again in a little while.';
  }
}
